import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar as RechartsRadar,
  ResponsiveContainer, PolarRadiusAxis, Tooltip as RechartsTooltip,
} from "recharts";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Link } from "wouter";
import {
  Target, CheckCircle2, AlertCircle, Info, ArrowLeft,
  ExternalLink, Building2, TrendingUp, RotateCcw,
  User, Mail, Phone, Globe, BookOpen, Send, Gift, MessageSquare,
  CheckCircle, Loader2, Download, BookmarkCheck, Bookmark, Pencil,
  LogIn, UserPlus,
} from "lucide-react";
import { CtaPanel } from "@/components/cta-panel";
import {
  normaliseCriterion,
  buildChartData,
  getGapVariant,
} from "@/lib/radar-scoring";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import type { RadarConfig, RadarCriterion, RadarEvaluation, RadarResponse, ActionCardPack, ActionCardIncentive } from "@shared/schema";

interface RadarDetail {
  config: RadarConfig & {
    company?: {
      id: string; name: string; slug?: string | null;
      website?: string | null; description?: string | null;
      logo?: string | null;
    } | null;
    supplierLeadPerson?: {
      id: string; name: string;
      firstName?: string | null; lastName?: string | null;
      jobTitle?: string | null; email?: string | null;
      slug?: string | null; avatar?: string | null;
    } | null;
    /** Article-linked fallback URLs — resolved server-side when articleId is set */
    articleLandingPageUrl?: string | null;
    articleProductUrl?: string | null;
    articleDatasheetUrl?: string | null;
  };
  criteria: RadarCriterion[];
}

interface EvaluationDetail {
  evaluation: RadarEvaluation;
  responses: RadarResponse[];
  criteria: RadarCriterion[];
}

function OutcomeBadge({ outcome, threshold }: { outcome: string | null; threshold: number }) {
  if (outcome === "match") {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-semibold text-lg">Well Aligned</span>
        </div>
        <span className="text-sm text-muted-foreground">Score ≥ {threshold}%</span>
      </div>
    );
  }
  if (outcome === "near_threshold") {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
          <Info className="h-5 w-5" />
          <span className="font-semibold text-lg">Near Threshold</span>
        </div>
        <span className="text-sm text-muted-foreground">Score {threshold - 10}–{threshold - 1}%</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
        <AlertCircle className="h-5 w-5" />
        <span className="font-semibold text-lg">Mismatched</span>
      </div>
      <span className="text-sm text-muted-foreground">Score &lt; {threshold - 10}%</span>
    </div>
  );
}

function GapBar({ normalised, variant }: { normalised: number; variant: string }) {
  const color =
    variant === "strong" ? "bg-green-500"
    : variant === "near" ? "bg-blue-500"
    : "bg-slate-400";
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${normalised}%` }} />
    </div>
  );
}

function GapStatusIcon({ variant }: { variant: string }) {
  if (variant === "strong") return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />;
  if (variant === "near") return <Info className="h-4 w-4 text-blue-500 shrink-0" />;
  return <AlertCircle className="h-4 w-4 text-slate-400 shrink-0" />;
}

function PersonInitials({ name }: { name: string }) {
  const parts = name.trim().split(" ");
  const initials = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
  return (
    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 font-semibold text-sm text-primary">
      {initials}
    </div>
  );
}

export default function RadarResultsPage() {
  const { slug, evaluationId } = useParams<{ slug: string; evaluationId: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  // Config / supplier lead come from the landing-page endpoint (no gap text needed)
  const { data: radarData, isLoading: radarLoading } = useQuery<RadarDetail>({
    queryKey: [`/api/public/radars/${slug}`],
    enabled: !!slug,
  });

  // Full criteria (with gap text) + responses come from the evaluation endpoint
  const { data: evalData, isLoading: evalLoading, refetch: refetchEval } = useQuery<EvaluationDetail>({
    queryKey: [`/api/public/evaluations/${evaluationId}`],
    enabled: !!evaluationId,
  });

  const claimMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", `/api/radar/evaluations/${evaluationId}/claim`),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["/api/account/evaluations"] });
      toast({ title: "Saved to your account", description: "Find this evaluation in My Radar Evaluations." });
    },
    onError: (err: any) => {
      toast({ title: "Could not save", description: err.message ?? "Please try again.", variant: "destructive" });
    },
  });

  // Detect if already claimed to current user
  const alreadySaved = !!(evalData?.evaluation?.userId && isAuthenticated && user && evalData.evaluation.userId === user.id);

  if (radarLoading || evalLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full space-y-6">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-6 w-80" />
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!radarData || !evalData) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h1 className="text-xl font-bold mb-2">Results Not Found</h1>
          <Button asChild variant="outline">
            <Link href="/radar"><ArrowLeft className="mr-2 h-4 w-4" />Back to Radar</Link>
          </Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const { config } = radarData;
  const { evaluation, responses, criteria } = evalData;
  const threshold = config.matchThreshold ?? 65;
  const overallScore = Math.round(evaluation.overallScore ?? 0);

  const outcomeColorClass =
    evaluation.matchOutcome === "match"
      ? "from-green-500/20 to-green-500/5 border-green-300 dark:border-green-700"
      : evaluation.matchOutcome === "near_threshold"
      ? "from-blue-500/10 to-blue-500/5 border-blue-200 dark:border-blue-800"
      : "from-slate-500/10 to-slate-500/5 border-slate-200 dark:border-slate-700";

  const scoreColorClass =
    evaluation.matchOutcome === "match" ? "text-green-600 dark:text-green-400"
    : evaluation.matchOutcome === "near_threshold" ? "text-blue-600 dark:text-blue-400"
    : "text-slate-600 dark:text-slate-400";

  // Build chart from shared scoring module — exclude skipped responses
  const skippedIds = new Set(responses.filter(r => r.skipped).map(r => r.criterionId));
  const responseMap: Record<string, number> = {};
  responses.forEach(r => { if (!r.skipped) responseMap[r.criterionId] = r.value; });
  const activeCriteria = criteria.filter(c => !skippedIds.has(c.id));
  const chartData = buildChartData(activeCriteria, responseMap);
  const skippedCount = skippedIds.size;

  // Build per-criterion gap data using the centralized scoring functions
  const criteriaWithGap = activeCriteria.map(c => {
    const resp = responses.find(r => r.criterionId === c.id && !r.skipped);
    const normalised = resp ? normaliseCriterion(resp.value, c.minValue ?? 0, c.maxValue ?? 10) : 0;
    const userWeight = resp?.userWeight ?? 1;
    const variant = getGapVariant(normalised, threshold);
    const gapText =
      variant === "strong" ? c.gapStrongText
      : variant === "near"   ? c.gapNearText
      : c.gapMissText;
    return { criterion: c, normalised, userWeight, variant, gapText };
  });

  const supplierLead = config.supplierLeadPerson;

  const lastModDate = evaluation.lastModifiedAt
    ? new Date(evaluation.lastModifiedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  const isSaved = saved || alreadySaved;

  const handleSave = () => {
    if (!isAuthenticated) {
      // Redirect to signup with return URL
      const returnUrl = encodeURIComponent(`/radar/${slug}/results/${evaluationId}`);
      setLocation(`/signup?redirect=${returnUrl}`);
      return;
    }
    claimMutation.mutate();
  };

  const handleDownload = () => {
    window.open(`/api/radar/evaluations/${evaluationId}/pdf`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-10 w-full">
        <Link href={`/radar/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to {config.title}
        </Link>

        <div className="mb-2 flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <span className="text-xs font-semibold text-primary uppercase tracking-wider">Evaluation Results</span>
        </div>
        <h1 className="text-2xl font-bold mb-1">{config.title}</h1>
        <p className="text-muted-foreground mb-8">{config.productName}</p>

        {/* Score card */}
        {config.bootstrapMode === "simple" && (
          <p className="text-sm text-muted-foreground mb-4 border-l-2 border-primary/30 pl-3 italic">
            Application fit profile — showing how many of this product's defining characteristics matter to your application.
          </p>
        )}
        <Card className={`mb-6 bg-gradient-to-br border ${outcomeColorClass}`}>
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {config.bootstrapMode === "simple" ? "Application Fit Score" : "Your Match Score"}
                </p>
                <div className="flex items-end gap-2 mb-3">
                  <span className={`text-6xl font-extrabold leading-none ${scoreColorClass}`} data-testid="text-overall-score">
                    {overallScore}
                  </span>
                  <span className="text-2xl text-muted-foreground mb-1">/ 100</span>
                </div>
                <OutcomeBadge outcome={evaluation.matchOutcome} threshold={threshold} />
                {lastModDate && (
                  <p className="text-xs text-muted-foreground mt-2">Last modified {lastModDate}</p>
                )}
              </div>
              <div className="text-sm text-muted-foreground space-y-1 sm:text-right">
                {config.bootstrapMode === "simple" ? (
                  <>
                    <p>
                      <strong className={`${scoreColorClass}`}>
                        {criteriaWithGap.filter(c => c.variant === "strong").length} of {activeCriteria.length}
                      </strong> characteristics matter highly
                    </p>
                    <p className="text-muted-foreground/70">to your application</p>
                  </>
                ) : (
                  <>
                    <p>{activeCriteria.length} criteria evaluated</p>
                    {skippedCount > 0 && (
                      <p className="text-muted-foreground/70">{skippedCount} skipped — not applicable</p>
                    )}
                    <p>Match threshold: <strong className="text-foreground">{threshold}%</strong></p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            onClick={handleDownload}
            variant="outline"
            size="sm"
            data-testid="button-download-report"
          >
            <Download className="mr-1.5 h-4 w-4" />
            Download Report
          </Button>

          {isSaved ? (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="text-green-700 dark:text-green-400 border-green-300 dark:border-green-700"
              data-testid="button-saved-indicator"
            >
              <Link href="/account/evaluations">
                <BookmarkCheck className="mr-1.5 h-4 w-4" />
                Saved — View My Evaluations
              </Link>
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              variant="outline"
              size="sm"
              disabled={claimMutation.isPending}
              data-testid="button-save-to-account"
            >
              {claimMutation.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Bookmark className="mr-1.5 h-4 w-4" />
              )}
              Save to My Account
            </Button>
          )}

          <Button
            asChild
            variant="outline"
            size="sm"
            data-testid="button-modify-answers"
          >
            <Link href={`/radar/${slug}/evaluate?evaluation=${evaluationId}`}>
              <Pencil className="mr-1.5 h-4 w-4" />
              Modify My Answers
            </Link>
          </Button>
        </div>

        {/* Guest prompt card — shown when not authenticated and not yet saved */}
        {!isAuthenticated && !isSaved && (
          <Card className="mb-8 border-primary/30 bg-primary/5">
            <CardContent className="pt-5 pb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-semibold text-sm mb-0.5">Save this evaluation to your account</p>
                <p className="text-sm text-muted-foreground">
                  Create a free account to revisit your results, modify your answers, and track all your Radar assessments.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  asChild
                  data-testid="button-signup-to-save"
                >
                  <Link href={`/signup?redirect=${encodeURIComponent(`/radar/${slug}/results/${evaluationId}`)}`}>
                    <UserPlus className="mr-1.5 h-4 w-4" />
                    Sign Up Free
                  </Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                  data-testid="button-login-to-save"
                >
                  <Link href={`/login?redirect=${encodeURIComponent(`/radar/${slug}/results/${evaluationId}`)}`}>
                    <LogIn className="mr-1.5 h-4 w-4" />
                    Log In
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Radar chart */}
        <Card className="mb-8">
          <CardHeader className="pb-0">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              {config.bootstrapMode === "simple" ? "Application Fit Profile" : "Evaluation Radar"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length >= 3 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={chartData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis
                    dataKey="subject"
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                  />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <RechartsRadar
                    name="Your Score"
                    dataKey="value"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.25}
                    strokeWidth={2}
                    dot={{ r: 3, fill: "hsl(var(--primary))" }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      fontSize: 12,
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                    }}
                    formatter={(v: number) => [`${v}%`, "Score"]}
                  />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">
                Not enough criteria for chart
              </div>
            )}
          </CardContent>
        </Card>

        {/* Unified per-criterion gap cards — score bar + gap text combined */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-base">Detailed Gap Analysis</CardTitle>
            <p className="text-sm text-muted-foreground">
              Score and guidance for each criterion you evaluated.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {criteriaWithGap.map(({ criterion, normalised, userWeight, variant, gapText }) => (
              <div
                key={criterion.id}
                className="p-4 rounded-lg border bg-muted/20"
                data-testid={`gap-criterion-${criterion.id}`}
              >
                {/* Header row */}
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <GapStatusIcon variant={variant} />
                    <span className="font-medium text-sm">{criterion.name}</span>
                    {criterion.isCritical && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Critical</Badge>
                    )}
                    {userWeight > 1 && (
                      <Badge variant="outline" className="text-[10px] px-1">×{userWeight}</Badge>
                    )}
                  </div>
                  <span className="text-sm font-semibold shrink-0">{normalised}%</span>
                </div>

                {/* Score bar */}
                <GapBar normalised={normalised} variant={variant} />

                {/* Threshold marker */}
                <div className="relative h-4 mt-0.5 mb-1">
                  <div
                    className="absolute top-0 w-px h-4 bg-border"
                    style={{ left: `${threshold}%` }}
                  />
                  <span
                    className="absolute top-1 text-[10px] text-muted-foreground"
                    style={{ left: `${threshold}%`, transform: "translateX(-50%)" }}
                  >
                    {threshold}%
                  </span>
                </div>

                {/* Gap text — framed as context, not failure */}
                {gapText && (
                  <div className="mt-3 pt-2 border-t border-border/50">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-1">
                      {variant === "strong" ? "Why this aligns" : "What this gap means for you"}
                    </p>
                    <p className="text-sm text-muted-foreground">{gapText}</p>
                  </div>
                )}

                {/* PSA supplementary text */}
                {variant !== "strong" && criterion.psaProblemText && (
                  <p className="text-xs text-muted-foreground/70 mt-1.5 italic">{criterion.psaProblemText}</p>
                )}
                {variant === "strong" && criterion.psaSolutionText && (
                  <p className="text-xs text-muted-foreground/70 mt-1.5 italic">{criterion.psaSolutionText}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Supplier CTA */}
        {config.company && (
          <Card className="mb-8 border-primary/30 bg-primary/5">
            <CardContent className="pt-6">
              {/* Application engineer */}
              {supplierLead && (
                <div className="flex items-start gap-3 mb-5 pb-5 border-b border-primary/15">
                  <PersonInitials name={supplierLead.name} />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-0.5">
                      Your application engineer at {config.company.name}
                    </p>
                    <p className="font-semibold">{supplierLead.name}</p>
                    {supplierLead.jobTitle && (
                      <p className="text-sm text-muted-foreground">{supplierLead.jobTitle}</p>
                    )}
                    {supplierLead.email ? (
                      <a
                        href={`mailto:${supplierLead.email}?subject=Radar Evaluation – ${config.productName}`}
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mt-1"
                        data-testid="link-contact-engineer"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        {supplierLead.email}
                      </a>
                    ) : supplierLead.slug ? (
                      <Button asChild variant="link" size="sm" className="p-0 h-auto mt-1" data-testid="link-engineer-profile">
                        <Link href={`/people/${supplierLead.slug}`}>
                          <User className="mr-1 h-3.5 w-3.5" />
                          View profile
                        </Link>
                      </Button>
                    ) : null}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  {config.company.logo ? (
                    <img
                      src={config.company.logo}
                      alt={config.company.name}
                      className="h-10 w-10 rounded object-contain shrink-0 bg-white border"
                      data-testid="img-company-logo"
                    />
                  ) : (
                    <Building2 className="h-8 w-8 text-primary shrink-0 mt-0.5" />
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{config.company.name}</h3>
                    {config.company.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {config.company.description}
                      </p>
                    )}
                    {evaluation.matchOutcome === "match" && (
                      <p className="text-sm text-green-600 dark:text-green-400 mt-2 font-medium">
                        Based on your evaluation, {config.company.name} appears to be well aligned with your application.
                      </p>
                    )}
                    {evaluation.matchOutcome === "near_threshold" && (
                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 font-medium">
                        There are areas worth exploring further — speak to {config.company.name} to understand how they address them.
                      </p>
                    )}
                    {evaluation.matchOutcome === "mismatch" && (
                      <p className="text-sm text-muted-foreground mt-2">
                        The gaps identified above are worth a conversation with {config.company.name} — some may have solutions specific to your application.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:shrink-0">
                  {config.company.website && (
                    <Button asChild data-testid="button-supplier-website">
                      <a href={config.company.website} target="_blank" rel="noopener noreferrer">
                        Visit Website
                        <ExternalLink className="ml-1.5 h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  {config.company.slug && (
                    <Button asChild variant="outline" data-testid="button-supplier-hub">
                      <Link href={`/companies/${config.company.slug}`}>
                        <Building2 className="mr-1.5 h-4 w-4" />
                        Company Profile
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA Library panel — composable CTAs attached to this radar */}
        <CtaPanel
          contentType="radar"
          contentId={radarData.config.id}
          context={{
            evaluationId,
            evaluationScore: overallScore,
            score: overallScore,
            productName: config.productName ?? undefined,
            companyName: config.company?.name ?? undefined,
            companyWebsite: config.company?.website ?? null,
            radarProductLandingPageUrl: config.productLandingPageUrl ?? null,
            radarProductDatasheetUrl: config.productDatasheetUrl ?? null,
            contactPersonName: config.supplierLeadPerson?.name ?? null,
            contactPersonPhotoUrl: config.supplierLeadPerson?.avatar ?? null,
            contactPersonTitle: config.supplierLeadPerson?.jobTitle ?? null,
            articleLandingPageUrl: config.articleLandingPageUrl ?? null,
            articleProductUrl: config.articleProductUrl ?? null,
            articleDatasheetUrl: config.articleDatasheetUrl ?? null,
          }}
          className="mb-6"
        />

        {/* Footer actions */}
        <div className="flex items-center gap-3 pt-4">
          <Button asChild variant="outline">
            <Link href={`/radar/${slug}/evaluate`}>
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Start New Evaluation
            </Link>
          </Button>
          <Button asChild variant="ghost">
            <Link href="/radar">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              All Radars
            </Link>
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

// ─── Enquiry Centre (legacy — kept for reference, not rendered) ──────────────

type CtaChannels = {
  enquiryForm?: boolean;
  linkedIn?: boolean;
  phone?: boolean;
  website?: boolean;
  catalogue?: boolean;
  introRequest?: boolean;
};

function _EnquiryCentre_UNUSED({
  pack,
  evaluationId,
  overallScore,
  companyName,
  companyWebsite,
  productName,
}: {
  pack: ActionCardPack & { incentive: ActionCardIncentive | null };
  evaluationId: string;
  overallScore: number;
  companyName?: string;
  companyWebsite?: string | null;
  productName: string;
}) {
  const channels: CtaChannels = (pack.enabledChannels as CtaChannels) ?? { enquiryForm: true };
  const incentive = pack.incentive;
  const incentiveUnlocked =
    incentive && overallScore >= (incentive.minScoreThreshold ?? 0);

  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [claimedIncentive, setClaimedIncentive] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", company: "", phone: "", message: "" });

  const mutation = useMutation({
    mutationFn: (claimingIncentive: boolean) =>
      apiRequest("POST", "/api/enquiries", {
        ...form,
        radarEvaluationId: evaluationId,
        claimedIncentiveId: claimingIncentive && incentive ? incentive.id : null,
      }),
    onSuccess: () => {
      setSubmitted(true);
      setShowForm(false);
    },
  });

  function openFormForIncentive() {
    setClaimedIncentive(true);
    setForm((f) => ({ ...f, message: incentive ? `I'd like to claim: ${incentive.offerTitle}` : f.message }));
    setShowForm(true);
  }

  if (submitted) {
    return (
      <Card className="mb-8 border-green-300 bg-green-50 dark:bg-green-900/20">
        <CardContent className="pt-6 pb-5 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-green-800 dark:text-green-300">Enquiry submitted</p>
            <p className="text-sm text-green-700 dark:text-green-400 mt-0.5">
              We've received your message and will be in touch shortly.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8 border-primary/20 bg-background" data-testid="card-enquiry-centre">
      {/* Trust strip */}
      {(pack.trustHostName || pack.trustHostPhoto) && (
        <div className="px-6 pt-5 pb-4 border-b border-border/60 flex items-center gap-3">
          {pack.trustHostPhoto ? (
            <img
              src={pack.trustHostPhoto}
              alt={pack.trustHostName ?? "Host"}
              className="h-12 w-12 rounded-full object-cover border shrink-0"
              data-testid="img-cta-host-photo"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <User className="h-5 w-5 text-primary" />
            </div>
          )}
          <div>
            {pack.trustHostName && (
              <p className="font-semibold text-sm" data-testid="text-cta-host-name">{pack.trustHostName}</p>
            )}
            {pack.trustTagline && (
              <p className="text-xs text-muted-foreground" data-testid="text-cta-host-tagline">{pack.trustTagline}</p>
            )}
          </div>
        </div>
      )}

      <CardContent className="pt-5 pb-5 space-y-5">
        <div>
          <p className="text-sm font-semibold mb-1">Next steps with {companyName ?? "the supplier"}</p>
          <p className="text-sm text-muted-foreground">
            Based on your evaluation, here are the ways you can connect.
          </p>
        </div>

        {/* Channel buttons */}
        <div className="flex flex-wrap gap-2">
          {channels.enquiryForm && (
            <Button
              variant="default"
              size="sm"
              onClick={() => { setClaimedIncentive(false); setShowForm((v) => !v); }}
              data-testid="button-channel-enquiry-form"
            >
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
              Send Enquiry
            </Button>
          )}
          {channels.website && companyWebsite && (
            <Button asChild variant="outline" size="sm" data-testid="button-channel-website">
              <a href={companyWebsite} target="_blank" rel="noopener noreferrer">
                <Globe className="mr-1.5 h-3.5 w-3.5" />
                Visit Website
              </a>
            </Button>
          )}
          {channels.phone && (
            <Button variant="outline" size="sm" data-testid="button-channel-phone">
              <Phone className="mr-1.5 h-3.5 w-3.5" />
              Request a Call
            </Button>
          )}
          {channels.catalogue && (
            <Button variant="outline" size="sm" data-testid="button-channel-catalogue">
              <BookOpen className="mr-1.5 h-3.5 w-3.5" />
              Get Catalogue
            </Button>
          )}
        </div>

        {/* Incentive card */}
        {incentiveUnlocked && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 flex items-start gap-3">
            <Gift className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-amber-800 dark:text-amber-300" data-testid="text-incentive-title">
                {incentive!.offerTitle}
              </p>
              {incentive!.offerDescription && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5" data-testid="text-incentive-desc">
                  {incentive!.offerDescription}
                </p>
              )}
              <div className="mt-3">
                {incentive!.ctaUrl ? (
                  <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="button-incentive-url">
                    <a href={incentive!.ctaUrl} target="_blank" rel="noopener noreferrer">
                      {incentive!.ctaLabel}
                      <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                    </a>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={openFormForIncentive}
                    data-testid="button-incentive-claim"
                  >
                    {incentive!.ctaLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Inline enquiry form */}
        {showForm && (
          <>
            <Separator />
            <div className="space-y-3" data-testid="form-enquiry">
              <p className="text-sm font-medium">
                {claimedIncentive && incentive
                  ? `Claim: ${incentive.offerTitle}`
                  : `Send an enquiry about ${productName}`}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={form.fullName}
                    onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                    placeholder="Your name"
                    data-testid="input-enquiry-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@company.com"
                    data-testid="input-enquiry-email"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Company</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  placeholder="Your company"
                  data-testid="input-enquiry-company"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  rows={3}
                  placeholder="Tell us about your application..."
                  data-testid="textarea-enquiry-message"
                />
              </div>
              <Button
                onClick={() => mutation.mutate(claimedIncentive)}
                disabled={mutation.isPending || !form.fullName || !form.email}
                data-testid="button-submit-enquiry"
              >
                {mutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                {claimedIncentive ? "Claim & Submit" : "Send Enquiry"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
